import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function useBatch(batchId, onFinished) {
  const { setCredits, refreshCredits } = useAuth();
  const [batch, setBatch] = useState(null);
  const [tick, setTick] = useState(0);
  const cb = useRef(onFinished);
  cb.current = onFinished;
  useEffect(() => {
    if (!batchId) { setBatch(null); return undefined; }
    let alive = true;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/batches/${batchId}`);
        if (!alive) return;
        setBatch(data);
        if (data.finished) {
          refreshCredits();
          if (data.summary?.notice) toast.warning(data.summary.notice, { duration: 12000 });
          cb.current?.(data);
          return;
        }
      } catch (e) {
        if (alive) setBatch({ error: errorMessage(e), files: [] });
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => { alive = false; clearTimeout(timer); };
  }, [batchId, tick, refreshCredits]);
  const retry = useCallback(async (extractionId) => {
    try {
      const { data } = await api.post(`/extractions/${extractionId}/retry`);
      if (data.balance !== undefined) setCredits(data.balance);
      setBatch((b) => b && { ...b, finished: false, files: b.files.map((f) => (f.extraction_id === extractionId ? { ...f, status: "pending", error: null } : f)) });
      setTick((t) => t + 1);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }, [setCredits]);
  return { batch, retry };
}

const CHUNK_TRIES = 6;
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

// Phase 23: chunked, resumable upload (8 MB parts; a failed part is retried, already-sent parts are skipped on resume).
async function uploadFile(file, onBytes) {
  const { data: u } = await api.post("/uploads", { filename: file.name, size: file.size });
  const done = new Set();
  for (let attempt = 0; done.size < u.chunks; attempt += 1) {
    if (attempt > 0) {
      await wait(Math.min(1000 * 2 ** attempt, 15000));
      try { (await api.get(`/uploads/${u.upload_id}`)).data.received.forEach((n) => done.add(n)); } catch { /* offline: retry */ }
    }
    if (attempt >= CHUNK_TRIES) throw new Error(`Upload of ${file.name} failed. Please check your connection and try again.`);
    try {
      for (let n = 0; n < u.chunks; n += 1) {
        if (done.has(n)) continue;
        const part = file.slice(n * u.chunk_size, Math.min(file.size, (n + 1) * u.chunk_size));
        await api.put(`/uploads/${u.upload_id}/chunks/${n}`, part, { headers: { "Content-Type": "application/octet-stream" }, timeout: 120000 });
        done.add(n);
        onBytes(Math.min(file.size, done.size * u.chunk_size));
      }
    } catch (e) {
      if (e.response && e.response.status >= 400 && e.response.status < 500) throw e;
    }
  }
  return u.upload_id;
}

export async function startBatch(files, quoteId, onProgress) {
  const total = files.reduce((s, f) => s + f.size, 0) || 1;
  const sent = files.map(() => 0);
  const report = () => onProgress?.(Math.round((100 * sent.reduce((a, b) => a + b, 0)) / total));
  report();
  const ids = [];
  for (let i = 0; i < files.length; i += 1) {
    ids.push(await uploadFile(files[i], (b) => { sent[i] = b; report(); }));
  }
  const { data } = await api.post("/batches/uploads", { upload_ids: ids, quote_id: quoteId || "" });
  return data;
}
